import type { TFunction } from "i18next";
import { PartNo, TExistingLineQuantity } from "../contexts/types";
import { resolveDisplayPartNoByKey } from "./utils";

/**
 * 生産キャパグラフ（既存ライン含む）の配色定義・グラフデータ組み立て・Excel出力行
 * Simulation画面（新製品・生産性向上の増産）と申請書（保存済みデータの表示時）で共通に使う
 */

/** 既存品（手入力PartNo）の棒の色。寒色系で行ごとの明暗差を大きめに取る。入力は最大3行のため3色 */
export const EXISTING_LINE_BAR_COLORS = ["#2e75b6", "#7fb2e5", "#bcd7f0"];

/**
 * 案件品（販売個数のPartNo）の棒の色。PartNo数に応じて循環（上限なし。7件目以降は色が重複する）
 * 赤い折れ線が埋もれないよう明るい暖色系にする。1色目は他グラフ（稼働日分生産個数）と同じオレンジ
 */
export const PROJECT_BAR_COLORS = ["#ff9f40", "#ffcd56", "#f4b183", "#e0a050", "#ffe699", "#c9a27a"];

/** 生産能力の折れ線の色（上限の意味で赤） */
export const CAPACITY_LINE_COLOR = "red";

/**
 * 保存済みの生産キャパグラフのデータに現行の配色・描画順を適用する（申請書用）
 * 配色変更前に保存されたデータも現行の見た目で表示するため
 * - 折れ線: 赤、太さは他グラフと同じ既定値、棒より前面
 * - 棒: 既存品／案件品それぞれのパレットを先頭から割り当て直す（既存品の判定は existingLine フラグ）
 *   フラグのない旧データは既存品と案件品を区別できないため、棒の色は保存時のまま
 */
export const applyExistingLineCapacityChartStyle = (chartData: any) => {
  const datasets: any[] = chartData?.datasets ?? [];
  const hasExistingLineFlag = datasets.some(dataset => dataset.existingLine !== undefined);
  let existingIndex = 0;
  let projectIndex = 0;

  datasets.forEach(dataset => {
    if (dataset.type === "line") {
      dataset.borderColor = CAPACITY_LINE_COLOR;
      dataset.backgroundColor = CAPACITY_LINE_COLOR;
      delete dataset.borderWidth;
      dataset.order = 0;
      return;
    }

    dataset.order = 1;
    if (!hasExistingLineFlag) return;
    dataset.backgroundColor = dataset.existingLine
      ? EXISTING_LINE_BAR_COLORS[existingIndex++ % EXISTING_LINE_BAR_COLORS.length]
      : PROJECT_BAR_COLORS[projectIndex++ % PROJECT_BAR_COLORS.length];
  });
};

/**
 * 生産キャパグラフ1版分（当初計画／ダウンサイド／保存済み設定のいずれか）の元データ
 * - quantityByPartNo: 案件品のPartNo別数量（length で切った後の値）
 * - capacity: 生産能力（全期間。length で切って使う）
 * - length: 表示する月数（ダウンサイドの期間短縮後。全系列をこの長さで切る）
 */
export type TExistingLineCapacityVersion = {
  quantityByPartNo: Record<string, number[]>;
  capacity: number[];
  length: number;
};

/** 生産キャパグラフの元データ（各画面の既存グラフの計算から切り出す） */
export type TExistingLineCapacitySource = {
  labels: string[];
  original: TExistingLineCapacityVersion;
  modified: TExistingLineCapacityVersion;
  savedConfigs: (TExistingLineCapacityVersion & { configId: string })[];
};

/**
 * 生産キャパグラフのチャートデータを組み立てる
 * 積み上げ棒 = 既存品（手入力・固定値）＋案件品（PartNo別数量）、折れ線 = 生産能力
 * labels は全期間のまま、data を length で切る（ダウンサイドの期間短縮。既存グラフと同じ方式）
 * @param labels 全期間の年月ラベル
 * @param existingLineQuantityData 既存ライン生産数量
 * @param version 元データ（1版分）
 * @param capexPartNo 案件のPartNo一覧（表示用PartNoの解決）
 * @param capacityLabel 折れ線のラベル
 */
export const buildExistingLineCapacityChartData = (
  labels: string[],
  existingLineQuantityData: TExistingLineQuantity,
  version: TExistingLineCapacityVersion,
  capexPartNo: Pick<PartNo, "partNo" | "displayPartNo">[],
  capacityLabel: string,
) => ({
  labels,
  datasets: [
    ...existingLineQuantityData.map((row, i) => ({
      type: "bar",
      label: row.partNo,
      data: labels.slice(0, version.length).map(label => Number(row.quantities[label] ?? 0) || 0),
      backgroundColor: EXISTING_LINE_BAR_COLORS[i % EXISTING_LINE_BAR_COLORS.length],
      stack: "qty",
      order: 1,
      // 申請書で既存品／案件品を判別して配色するためのフラグ
      existingLine: true,
    })),
    ...Object.entries(version.quantityByPartNo).map(([partNo, data], i) => ({
      type: "bar",
      label: resolveDisplayPartNoByKey(partNo, capexPartNo),
      data,
      backgroundColor: PROJECT_BAR_COLORS[i % PROJECT_BAR_COLORS.length],
      stack: "qty",
      order: 1,
      existingLine: false,
    })),
    {
      type: "line",
      label: capacityLabel,
      data: version.capacity.slice(0, version.length),
      // 太さは他グラフと同じく Chart.js の既定値
      borderColor: CAPACITY_LINE_COLOR,
      backgroundColor: CAPACITY_LINE_COLOR,
      fill: false,
      tension: 0.4,
      // 折れ線を棒より前面に描画する（order が小さいほど手前）
      order: 0,
    },
  ],
});

/**
 * 生産キャパグラフの内訳をExcel出力用の行に変換する（既存シートの末尾に追加するセクション）
 * グラフと同じ元データ・同じ期間（ダウンサイドは短縮後）で出力する
 * @param t 翻訳関数
 * @param existingLineQuantityData 既存ライン生産数量
 * @param version 元データ（1版分）
 * @param labels 全期間の年月ラベル
 * @param sheetTitle シートタイトル（例: "当初計画", "ダウンサイド", "設定A"）
 * @param capexPartNo 案件のPartNo一覧（表示用PartNoの解決）
 * @returns 2次元配列（ヘッダー行＋既存品行＋案件品行＋合計・生産能力・差分）
 */
export const createExistingLineCapacitySectionRows = (
  t: TFunction,
  existingLineQuantityData: TExistingLineQuantity,
  version: TExistingLineCapacityVersion,
  labels: string[],
  sheetTitle: string,
  capexPartNo: Pick<PartNo, "partNo" | "displayPartNo">[],
): (string | number)[][] => {
  const outputLabels = labels.slice(0, version.length);

  // 既存品（手入力・固定値）。未入力・数値以外は0（グラフと同じ扱い）
  const existingValues = existingLineQuantityData.map(row =>
    outputLabels.map(label => Number(row.quantities[label] ?? 0) || 0)
  );
  // 案件品（PartNo別数量）
  const projectEntries = Object.entries(version.quantityByPartNo);
  const projectValues = projectEntries.map(([, data]) =>
    outputLabels.map((_, i) => Number(data[i] ?? 0) || 0)
  );

  const totals = outputLabels.map((_, i) =>
    [...existingValues, ...projectValues].reduce((sum, values) => sum + values[i], 0)
  );
  const capacities = outputLabels.map((_, i) => Number(version.capacity[i] ?? 0) || 0);

  return [
    [t('features.InvestmentScreening.SimulationNewProduct.excel.existingLineCapacitySection', { title: sheetTitle }), ...outputLabels],
    ...existingLineQuantityData.map((row, i) => [
      t('features.InvestmentScreening.SimulationNewProduct.excel.existingLineQuantityRow', { partNo: row.partNo }),
      ...existingValues[i],
    ]),
    ...projectEntries.map(([partNo], i) => [
      t('features.InvestmentScreening.SimulationNewProduct.excel.projectQuantityRow', { partNo: resolveDisplayPartNoByKey(partNo, capexPartNo) }),
      ...projectValues[i],
    ]),
    [t('features.InvestmentScreening.SimulationNewProduct.excel.existingLineTotalQuantity'), ...totals],
    [t('features.InvestmentScreening.SimulationNewProduct.excel.existingLineProductionCapacity'), ...capacities],
    [t('features.InvestmentScreening.SimulationNewProduct.excel.existingLineSurplusCapacity'), ...capacities.map((c, i) => c - totals[i])],
  ];
};
